import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useAuth0 } from "@auth0/auth0-react";
import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Loader2, CheckCircle, AlertCircle } from "lucide-react";
import { handleAuth0Error } from "@/lib/auth/auth-errors";
import { withReducedMotion } from "@/lib/animations";

export const Route = createFileRoute("/auth/callback")({
  head: () => ({
    meta: [
      {
        name: "robots",
        content: "noindex, nofollow",
      },
    ],
  }),
  component: AuthCallback,
});

type CallbackState = "loading" | "success" | "error";

function AuthCallback() {
  const { isLoading, error, isAuthenticated, handleRedirectCallback } = useAuth0();
  const navigate = useNavigate();
  const [state, setState] = useState<CallbackState>("loading");
  const [errorMessage, setErrorMessage] = useState("");
  const [showSuccess, setShowSuccess] = useState(false);

  useEffect(() => {
    const processCallback = async () => {
      // Check for error in URL params
      const params = new URLSearchParams(window.location.search);
      const urlError = params.get("error");
      const urlErrorDescription = params.get("error_description");

      if (urlError) {
        setErrorMessage(urlErrorDescription || "Authentication failed");
        setState("error");
        return;
      }

      // Handle Auth0 redirect callback if there's a code
      const code = params.get("code");
      if (code) {
        try {
          await handleRedirectCallback();
        } catch (err) {
          const authError = handleAuth0Error(err);
          setErrorMessage(authError.message);
          setState("error");
          return;
        }
      }
    };

    processCallback();
  }, [handleRedirectCallback]);

  // Handle successful authentication
  useEffect(() => {
    if (!isLoading && isAuthenticated && state !== "error") {
      setState("success");
      setShowSuccess(true);

      // Get redirect URL from sessionStorage or appState
      const redirectUrl = sessionStorage.getItem("auth_redirect") || "/app/dashboard";
      sessionStorage.removeItem("auth_redirect");

      // Brief delay to show success state
      const timer = setTimeout(() => {
        navigate({ to: redirectUrl });
      }, 1000);

      return () => clearTimeout(timer);
    }
  }, [isLoading, isAuthenticated, navigate, state]);

  // Handle Auth0 SDK errors
  useEffect(() => {
    if (error) {
      const authError = handleAuth0Error(error);
      setErrorMessage(authError.message);
      setState("error");
    }
  }, [error]);

  // Timeout handler for long loads
  useEffect(() => {
    if (state === "loading") {
      const timeout = setTimeout(() => {
        if (state === "loading") {
          setErrorMessage("Authentication is taking longer than expected. Please try again.");
          setState("error");
        }
      }, 30000); // 30 second timeout

      return () => clearTimeout(timeout);
    }
  }, [state]);

  const handleRetry = () => {
    navigate({ to: "/auth/login", search: { redirect: "/app/dashboard" } });
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-gradient-to-b from-purple-50 to-white dark:from-slate-900 dark:to-slate-950">
      <div className="text-center">
        {state === "loading" && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            variants={withReducedMotion({
              hidden: { opacity: 0 },
              visible: { opacity: 1 },
            })}
          >
            <Loader2 className="mx-auto h-12 w-12 animate-spin text-purple-500" />
            <p className="mt-4 text-slate-600 dark:text-slate-400">
              Completing sign in...
            </p>
          </motion.div>
        )}

        {state === "success" && showSuccess && (
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.3 }}
          >
            <CheckCircle className="mx-auto h-12 w-12 text-green-500" />
            <p className="mt-4 text-slate-600 dark:text-slate-400">
              Welcome back! Redirecting...
            </p>
          </motion.div>
        )}

        {state === "error" && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-md px-4"
          >
            <AlertCircle className="mx-auto h-12 w-12 text-red-500" />
            <h2 className="mt-4 text-xl font-semibold text-slate-900 dark:text-white">
              Authentication Error
            </h2>
            <p className="mt-2 text-slate-600 dark:text-slate-400">
              {errorMessage}
            </p>
            <button
              onClick={handleRetry}
              className="mt-6 rounded-lg bg-purple-500 px-6 py-2 font-semibold text-white transition-colors hover:bg-purple-600 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 dark:focus:ring-offset-slate-900"
            >
              Try Again
            </button>
          </motion.div>
        )}
      </div>
    </div>
  );
}
